/**
 * @React
 */
import React, { FC, ReactNode, useEffect, useState, useCallback, useRef } from "react";

import { useHistory } from "react-router-dom";
import { useAuth } from "context/AuthContext";
/**
 * @MUI
 */
import { Box, Button, IconButton, Grid } from "@mui/material";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AddIcon from '@mui/icons-material/Add';
/**
 * @Components
 */
import Loader from "components/Loader";
import CardFieldSet from "components/CustomComponents/CardFieldSet";
import { MonitoringLogsDetailsCgs } from "components/detailedApplication/MonitoringApplication";
import { IMonitoringCgsFields } from "types/monitoringCgsFields";
import CustomField from "components/CustomComponents/CustomFields/input";
import AutoComplete from "components/CustomComponents/CustomFields/AutoComplete";
import FilesContainer from "components/CustomComponents/FilesContainer";
import FileUploadDropbox from "components/dialogs/FileUploadDropbox";
/**
 * @ReactHookForm
 */
import { Controller, FormProvider, SubmitErrorHandler, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
/**
 * @Types
 */
import {
  IMonitoringFields,
  IInputField,
  IMonitoringAgreementModel,
  UploadedAttachments,
  IDifferenceFromAgreement,
  IMonitoringAgreementCgsModel,
  AttachmentKey,
  AttachmentFileDraftCGS,
  ATTACHMENT_KEYS,
} from "types";
/**
 * @Hooks
 */
import { useFormCalculations } from "hooks/useMonitoringCgsHook";
import { useDependentOptions } from "hooks/useDepenentOptionsMonitoring";
/**
 * @Utilities
 */
import { getFirstErrorMessage } from "utilities/Fields/monitoringFields/utils";
import { monitoringCgsGroupFieldsInitValues } from "utilities/Fields/CgsMonitoringFields/fields/forms";
import { generateUrl } from "utilities/generateRequestUrl";
import axiosInstance from "utilities/axios";
import config from "utilities/generateRequestUrl/config";
import { monitoringCgsInitialDataSchema } from "utilities/Fields/CgsMonitoringFields/fields/forms/validation";
import { fieldsCgs, valueFormatCgs } from "utilities/Fields/CgsMonitoringFields/fields/changesFields";
import DetailTableTR from "components/CustomComponents/TablesCompoent/Detail/DetailTableTR";
import DetailePageStyles from "utilities/DetailePageStayles";
import DynamicFieldArray from "./DynamicFieldArray";
import ExpenseGroupsTable from "./ExpenseGroupsTable";

interface MonitoringCgsFormProps {
  data: IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>;
  load: boolean;
  url: string;
  opts?: any;
  generatedValues?: any;
}
const MonitoringCgsForm: FC<MonitoringCgsFormProps> = ({ data, load, url, opts, generatedValues }) => {
  const history = useHistory();
  const { user: currentUser } = useAuth();
  const classes = DetailePageStyles();
  const [submitReload, setSubmitReload] = useState<boolean>(false);
  const [uploadedAttachments, setUploadedAttachments] = useState<UploadedAttachments>({ monitoring_attachments: [] });
  const [projectActivity, setProjectActivity] = useState<{ label: string; value: string } | null>(null);

  const methods = useForm<IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>>({
    defaultValues: (data as IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>) || {},
    resolver: yupResolver(monitoringCgsInitialDataSchema) as any,
    mode: "onBlur",
  });
  const { setValue, getValues, control, handleSubmit, watch, formState } = methods as any;

  const { errors } = formState;

  const {
    fields: FilesFields,
    append: AppendFile,
    remove: RemoveFile,
  } = useFieldArray({
    control,
    name: "attached_documents.monitoring_attachments",
  });
  const {
    fields: SignImageFields,
    append: AppendSignImage,
    remove: RemoveSignImage,
  } = useFieldArray({
    control,
    name: "attached_documents.sign_image",
  });
  let {
    fields: projectActivityFields,
    append: appendProjectActivity,
    remove: removeProjectActivity,
  } = useFieldArray({
    control,
    name: "project_activities",
  });
  const watchedValues = watch();
  const { visibilityConditions } = useFormCalculations(watchedValues, setValue, methods.control._defaultValues);
  const { optionsArray } = useDependentOptions(watchedValues.actual_municipality, opts.mountainous_settlements_per_municipality);

  const shouldHideField = (fieldName: keyof typeof visibilityConditions) => {
    return visibilityConditions[fieldName] === false; // Return true if the field should be hidden
  };

  const submitFn = async (data: IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>) => {
    try {
      setSubmitReload(true);
      const resp = await axiosInstance.post(generateUrl(config.client, url, null, null), {
        application: data,
      });

      history.push("/monitoring/cgs");
      
    } catch (e) {
      if (e) {
        alert("დაფიქსირდა შეცდომა");
      }
      console.error(e);
    } finally {
      setSubmitReload(false);
    }
  };

  const onError: SubmitErrorHandler<IMonitoringFields & Partial<IMonitoringAgreementModel>> = (errors) => {
    const message = getFirstErrorMessage(errors);
    alert(message);
  };

  const isExpenseTableReadOnly =
    data?.monitoring_approval_status === "APPROVED" ||
    data?.monitoring_approval_status === "REJECTED";

  const onSubmit = (data: IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>) => {
    const transformedData = { ...data };

    // Strip backend-calculated fields so server recomputes them
    if (Array.isArray(transformedData.expenseGroups)) {
      transformedData.expenseGroups = transformedData.expenseGroups.map(
        ({ calculatedAmount: _removed, ...rest }) => rest
      ) as any;
    }
    delete (transformedData as any).totalConfirmedExpense;

    const attachedDocs = transformedData.attached_documents;

    if (attachedDocs) {
      ATTACHMENT_KEYS.forEach((key) => {
        const files = attachedDocs[key] as AttachmentFileDraftCGS[];

        if (Array.isArray(files)) {
          attachedDocs[key] = files
            .filter(a => !!a.file_name && !!a.path)
            .map(({ file_name, path }: AttachmentFileDraftCGS) => ({
              file_name,
              path,
            }));
        }
      });
    }

    if (transformedData.attached_documents?.monitoring_attachments) {
      transformedData.attached_documents.monitoring_attachments = transformedData.attached_documents.monitoring_attachments.map((file: any) => {
        return typeof file === "string" ? file : file.original_path;
      });
    }

    // On create (submit), stamp the current user's active CGS assignment
    if (url === "monitoringCgsSubmit") {
      const cgsAssignment = currentUser?.activeAssignments?.find(
        (a) => a.application_type === "cgs"
      );
      if (cgsAssignment) {
        (transformedData as any).monitoring_assignment = cgsAssignment._id;
      }
    }

    submitFn(transformedData);
  };
  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <>
          {opts && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                width: `100%`,
              }}
            >
              <FormProvider {...(methods as any)}>
                {
                  <form onSubmit={handleSubmit(onSubmit, onError)} noValidate>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                        width: `100%`,
                      }}
                    >
                      {monitoringCgsGroupFieldsInitValues.map((group, groupIndex) => {
                        if (group.groupName === "fixed_assets_capital_expenditures_funds_turnover") {
                          return (
                            <CardFieldSet key={groupIndex} title={group.groupLabel}>
                              {group.fields.map((item: IInputField<IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>>, itemIndex) => {
                                if (shouldHideField(item.name as keyof typeof visibilityConditions)) {
                                  return null;
                                }
                                if (item.name === "amount_of_used_amount_table") {

                                  return (
                                    <>
                                      <table className={classes.table}>
                                        <thead>
                                          <tr>
                                            <th style={{ fontSize: "12px", textAlign: "left", padding: "8px", width: "33.33%" }}>
                                              პროექტის ფარგლებში განხორციელებული ხარჯვა;
                                            </th>
                                            <th style={{ fontSize: "12px", textAlign: "left", padding: "8px", width: "33.33%" }}>მიზნობრივი ხარჯვა;</th>
                                            <th colSpan={2} style={{ fontSize: "12px", textAlign: "left", padding: "8px", width: "33.33%" }}>
                                              არამიზნობრივი ხარჯვა;
                                              <i
                                                style={{
                                                  fontSize: "10px",
                                                  fontWeight: 100,
                                                  color: "rgb(0, 0, 0, 0.8)",
                                                }}
                                              >
                                                (მოიცავს შემდეგს: არ არის შეძენილი გუდვილი; არ არის შეძენილი სატრანსპორტო საშუალებები და არ განხორციელებულა
                                                მათი რემონტი, გარდა სპეციალური საშუალებებისა, რომლებიც უშუალოდ გამოიყენება საწარმოს ტერიტორიაზე
                                                პროდუქციის/მომსახურების წარმოების პროცესში; სახელმწიფო ან/და კერძო საკუთრებაში არსებული უძრავი ქონების შეძენა
                                                ან/და საქართველოს სამოქალაქო კოდექსით გათვალისწინებული ფორმებით დროებით სარგებლობაში აღება; კერძო სამართლის
                                                იურიდიული პირის აქციების ან წილების შეძენა ან მართვაში აღება; საქართველოს კანონმდებლობის შესაბამისად
                                                გათვალისწინებული სახდელების (ჯარიმა, საურავი, პირგასამტეხლო და სხვა) გადახდა/დაფარვა)
                                              </i>
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {item.tableSchema.map((row: IInputField<IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>>, index: number) => {
                                            const fields = row.fields ?? [];
                                            const attachmentField = fields.find((field: any) =>
                                              ATTACHMENT_KEYS.includes(field.name as AttachmentKey)
                                            );

                                            return (
                                              <React.Fragment key={`${groupIndex}-${itemIndex}-${index}`}>
                                                <tr
                                                  style={{
                                                    backgroundColor: "#fafafa",
                                                    borderTop: "1px solid rgba(0,0,0,0.12)",
                                                  }}
                                                >
                                                  {fields.map((field: any) => (
                                                    <td
                                                      key={`${groupIndex}-${itemIndex}-${index}-${field.name}`}
                                                      style={{
                                                        padding: "4px",
                                                        fontSize: "11px",
                                                        color: "rgba(0,0,0,0.54)",
                                                        borderBottom: "none",
                                                      }}
                                                      colSpan={field.colSpan ?? 1}
                                                    >
                                                      {field.label}
                                                    </td>
                                                  ))}
                                                  <td
                                                    style={{
                                                      padding: "4px",
                                                      fontSize: "11px",
                                                      color: "rgba(0,0,0,0.54)",
                                                      borderBottom: "none"
                                                    }}
                                                  >
                                                    {attachmentField ? "დოკუმენტი" : ""}
                                                  </td>
                                                </tr>

                                                {/* ───── Value row ───── */}
                                                <tr style={{ borderBottom: "1px solid rgb(0,0,0,0.23)" }}>
                                                  {fields.map((field: any, i: number) => {
                                                    return (
                                                      <td
                                                        key={field.name}
                                                        style={{ padding: "4px" }}
                                                        colSpan={field.colSpan ?? 1}
                                                      >
                                                        {field.disabled ? (
                                                          <Controller
                                                            name={field.name}
                                                            control={control}
                                                            render={({ field }) => {
                                                              return (
                                                                <output
                                                                  style={{
                                                                    fontSize: "11px",
                                                                    padding: "8px",
                                                                    height: "20px",
                                                                  }}
                                                                >
                                                                  {Number(field.value || 0)}
                                                                </output>
                                                              )
                                                            }}
                                                          />
                                                        ) : (
                                                          <>
                                                            {!shouldHideField(field.name) ? <CustomField
                                                              name={field.name}
                                                              label={""}
                                                              type={
                                                                (field.type as "text" | "select" | "textarea" | "checkbox" | "autocomplete" | "number" | "date") ||
                                                                "text"
                                                              }
                                                              control={control}
                                                              disabled={field.disabled}
                                                              errors={errors[field.name]}
                                                              width="150px"
                                                              inputSx={{ minWidth: "unset", height: "32px" }}
                                                            /> : null}
                                                          </>
                                                        )}
                                                      </td>
                                                    )
                                                  })}
                                                  <td style={{ padding: "4px" }}>
                                                    {attachmentField && attachmentField.name && (<>
                                                      {!shouldHideField(attachmentField.name) ? <DynamicFieldArray
                                                        control={control}
                                                        field_name={attachmentField.name}
                                                        setValue={setValue}
                                                      /> : null}
                                                    </>

                                                    )}
                                                  </td>
                                                </tr>
                                              </React.Fragment>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </>
                                  );
                                }
                                if (item.name === "project_activities") {
                                  const project_activities = opts[item.optionName ?? "cgs_nace_activities"] as { value: string; label: string }[];
                                  const projectActivitiesValues = generatedValues[item.optionName ?? "cgs_nace_activities"];
                                  const pr = projectActivityFields.map((projectActivity, index) => {
                                    return (
                                      <>
                                        <Controller
                                          key={projectActivity.id}
                                          name={`project_activities.${index}`}
                                          control={control}
                                          render={({ field }) => {
                                            const projectActivityData = Object.values(project_activities).find(
                                              (activity: { label: string; value: string }) => activity?.value === field.value // Find the activity by id
                                            );
                                            return (
                                              <Box
                                                sx={{
                                                  padding: "0 15px 0 50px",
                                                  width: "100%",
                                                }}
                                              >
                                                <table className={classes.table}>
                                                  <tbody>
                                                    <>
                                                      <DetailTableTR
                                                        label={projectActivityData ? `${projectActivityData?.label}` : `აქტივობა არ მოიძებნა (${field.value})`}
                                                        loading={false}
                                                      >
                                                        <IconButton
                                                          sx={{
                                                            color: "rgb(211, 47, 47)",
                                                          }}
                                                          onClick={() => {
                                                            removeProjectActivity(index);
                                                            setProjectActivity(projectActivityData || null);
                                                          }}
                                                        >
                                                          <RemoveOutlinedIcon
                                                            sx={{
                                                              fontSize: "12px",
                                                              color: "rgb(211, 47, 47)",
                                                            }}
                                                          />
                                                        </IconButton>
                                                      </DetailTableTR>
                                                      {projectActivityData &&
                                                        projectActivitiesValues[projectActivityData?.value]["program_compatibility"] === false ? (
                                                        <tr>
                                                          <td
                                                            style={{
                                                              padding: "4px",
                                                              width: "100%",
                                                              color: "rgb(211, 47, 47)",
                                                              fontSize: "11px",
                                                            }}
                                                          >
                                                            არ ჯდება პროგრამაში
                                                          </td>
                                                        </tr>
                                                      ) : null}
                                                    </>
                                                  </tbody>
                                                </table>
                                              </Box>
                                            );
                                          }}
                                        />
                                      </>
                                    );
                                  });
                                  const autoCompleteComponent = () => (
                                    <>
                                      <Grid
                                        container
                                        sx={{
                                          width: "100%",
                                          justifyContent: "center",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Grid item xs={11}>
                                          {project_activities && (
                                            <AutoComplete
                                              name={"activity"}
                                              value={projectActivity}
                                              label={item.label}
                                              options={Object.values(project_activities) as { value: string; label: string }[]}
                                              callback={(data) => {
                                                if (data) setProjectActivity(data);
                                              }}
                                            />
                                          )}
                                        </Grid>
                                        <Grid item xs={1} display="flex" justifyContent="center">
                                          <IconButton
                                            disabled={projectActivity ? false : true}
                                            onClick={() => {
                                              appendProjectActivity(projectActivity?.value);
                                              setProjectActivity(null);
                                            }}
                                            sx={{
                                              cursor: "pointer",
                                            }}
                                          >
                                            <AddIcon
                                              sx={{
                                                fontSize: 20,
                                                color: !!projectActivity ? "rgb(44, 91, 148, 0.8)" : "rgb(0,0,0,0.23)",
                                              }}
                                            />
                                          </IconButton>
                                        </Grid>
                                      </Grid>
                                      {pr}
                                    </>
                                  );
                                  return (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "left",
                                        gap: "20px",
                                      }}
                                    >
                                      {autoCompleteComponent()}
                                    </Box>
                                  );
                                }
                                if (item.name === "monitoring_new_deadline") {
                                  return (
                                    <>
                                      <Controller
                                        key={"monitoring_new_deadline"}
                                        name={"monitoring_new_deadline"}
                                        control={control}
                                        render={({ field }) => {
                                          return (
                                            <Box
                                              sx={{
                                                padding: "12px",
                                                width: "100%",
                                              }}
                                            >
                                              <DetailTableTR label={item.label} loading={false}>
                                                <>{field.value ? valueFormatCgs[field.name as keyof typeof valueFormatCgs](field.value) : ""}</>
                                              </DetailTableTR>
                                            </Box>
                                          );
                                        }}
                                      />
                                    </>
                                  );
                                }
                                if (item.type === "expense_groups") {
                                  return (
                                    <ExpenseGroupsTable key={itemIndex} />
                                  );
                                }
                                if (item.name === "is_taken_business_loan_leasing") {
                                  return (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "left",
                                        gap: "20px",
                                      }}
                                    >
                                      <CustomField
                                        name={item.name}
                                        label={item.label}
                                        value={item.value}
                                        type={(item.type as "text" | "select" | "textarea" | "checkbox" | "autocomplete" | "number" | "date") || "text"}
                                        control={control}
                                        options={[
                                          { value: true, label: "კი" },
                                          { value: false, label: "არა" },
                                        ]}
                                        errors={errors[item.name]}
                                      />
                                    </Box>
                                  );
                                }
                                if (item.name === "is_sign_inscription_banner") {
                                  return (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "left",
                                        gap: "20px",
                                      }}
                                    >
                                      <CustomField
                                        name={item.name}
                                        label={item.label}
                                        value={item.value}
                                        type={(item.type as "text" | "select" | "textarea" | "checkbox" | "autocomplete" | "number" | "date") || "text"}
                                        control={control}
                                        options={[
                                          { value: true, label: "კი" },
                                          { value: false, label: "არა" },
                                        ]}
                                        errors={errors[item.name]}
                                      />
                                      {watchedValues.is_sign_inscription_banner === true && (
                                        <DynamicFieldArray
                                          control={control}
                                          field_name="sign_image"
                                          setValue={setValue}
                                          autoGenerateName
                                        />
                                      )}
                                    </Box>
                                  );
                                }
                                return (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      justifyContent: "left",
                                      gap: "20px",
                                    }}
                                  >
                                    <CustomField
                                      name={item.name}
                                      label={item.label}
                                      type={(item.type as "text" | "select" | "textarea" | "checkbox" | "autocomplete" | "number" | "date") || "text"}
                                      control={control}
                                      {...(item.optionName && opts
                                        ? { options: Object.values(opts[item.optionName]) }
                                        : item.options
                                          ? { options: item.options }
                                          : {})}
                                      disabled={item.disabled}
                                      errors={errors[item.name]}
                                    />
                                  </Box>
                                );
                              })}
                            </CardFieldSet>
                          );
                        }
                        return (
                          <CardFieldSet key={groupIndex} title={group.groupLabel}>
                            <>
                              {group.fields.map((item: IInputField<IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>>) => {
                                if (shouldHideField(item.name as keyof typeof visibilityConditions)) {
                                  return null;
                                }
                                if (item.name === "mountainous_settlement") {
                                  return (
                                    <CustomField
                                      name={item.name}
                                      label={item.label}
                                      type={item.type as "text" | "select" | "textarea" | "checkbox" | "autocomplete" | "number" | "date" | "day"}
                                      control={control}
                                      options={optionsArray}
                                      disabled={item.disabled}
                                      errors={errors[item.name]}
                                    />
                                  );
                                }
                                return (
                                  <CustomField
                                    name={item.name}
                                    label={item.label}
                                    type={item.type as "text" | "select" | "textarea" | "checkbox" | "autocomplete" | "number" | "date" | "day"}
                                    control={control}
                                    {...(item.optionName && opts
                                      ? { options: Object.values(opts[item.optionName]) }
                                      : item.options
                                        ? { options: item.options }
                                        : {})}
                                    disabled={item.disabled}
                                    errors={errors[item.name]}
                                  />
                                );
                              })}
                            </>
                          </CardFieldSet>
                        );
                      })}

                      {
                        <CardFieldSet key={`100000001`} title={"ასატვირთი დოკუმენტები"}>
                          <>
                            <FileUploadDropbox
                        uploadUrl="/monitoring/application/upload_attachments/monitoring-cgs"
                              onUploadSuccess={(data) => {
                                if (data.monitoring_attachments) {
                                  data.monitoring_attachments.forEach((fileData: any) => {
                                    AppendFile(fileData);
                                  });
                                }
                              }}
                              fieldName="monitoring_attachments"
                              multiple={true}
                            />
                            <Controller
                              name="attached_documents.monitoring_attachments"
                              control={control}
                              render={() => (
                                <FilesContainer
                                  FilesList={methods.getValues("attached_documents.monitoring_attachments")}
                                  mode="form"
                                  removeCallback={(i) => {
                                    RemoveFile(i);
                                  }}
                                  cols={6}
                                />
                              )}
                            />
                          </>
                        </CardFieldSet>
                      }
                      {data && data?.difference_from_agreement && data?.difference_from_agreement.length > 0 && (
                        <CardFieldSet key={`10000002`} title={"განაცხადის ცვლილების ისტორია"}>
                          <>
                            <MonitoringLogsDetailsCgs
                              changedDataHistory={data?.difference_from_agreement ?? ([] as IDifferenceFromAgreement[])}
                              valueFormat={valueFormatCgs}
                              fields={fieldsCgs}
                            />
                          </>
                        </CardFieldSet>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          width: `100%`,
                        }}
                      >
                        <Button
                          type="submit"
                          variant="contained"
                          sx={{
                            width: "100%",
                          }}
                        >
                          გაგზავნა
                        </Button>
                      </Box>
                    </Box>
                  </form>
                }
              </FormProvider>
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default MonitoringCgsForm;

